"use client";

import { useEffect, useState } from "react";

/**
 * Hook to detect if the app is running as an installed PWA
 * 
 * Returns true when the app is:
 * - Installed and running in standalone mode (iOS/Android)
 * - Running in fullscreen mode
 * - Running as a minimal-ui app
 * 
 * @returns {boolean} True if running as PWA, false if in browser
 */
export function useIsPWA(): boolean {
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    // Check if running in standalone mode (PWA installed)
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
    
    // Check iOS standalone mode
    const isIOSStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    
    // Check if running in fullscreen or minimal-ui
    const isFullscreen = window.matchMedia("(display-mode: fullscreen)").matches;
    const isMinimalUI = window.matchMedia("(display-mode: minimal-ui)").matches;

    setIsPWA(isStandalone || isIOSStandalone || isFullscreen || isMinimalUI);
  }, []);

  return isPWA;
}

/**
 * Hook to detect the display mode of the app
 * 
 * @returns {"browser" | "standalone" | "fullscreen" | "minimal-ui"}
 */
export function useDisplayMode(): "browser" | "standalone" | "fullscreen" | "minimal-ui" {
  const [displayMode, setDisplayMode] = useState<"browser" | "standalone" | "fullscreen" | "minimal-ui">("browser");

  useEffect(() => {
    if (window.matchMedia("(display-mode: fullscreen)").matches) {
      setDisplayMode("fullscreen");
    } else if (window.matchMedia("(display-mode: standalone)").matches) {
      setDisplayMode("standalone");
    } else if (window.matchMedia("(display-mode: minimal-ui)").matches) {
      setDisplayMode("minimal-ui");
    } else {
      setDisplayMode("browser");
    }
  }, []);

  return displayMode;
}

